import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  TrashIcon,
  EyeIcon,
  BoltIcon,
  GlobeAltIcon,
  LockClosedIcon,
  ServerIcon,
  DocumentMagnifyingGlassIcon,
  PlusIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import securityScanService, { SecurityScan, DashboardSummary } from '../../services/api/security-scan.service';

type ScanType = 'ssl' | 'headers' | 'port' | 'dns' | 'dast' | 'pentest';

export default function SecurityDashboardPage() {
  const { t } = useTranslation('security');
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [scans, setScans] = useState<SecurityScan[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'scans'>('overview');
  const [showNewScanModal, setShowNewScanModal] = useState(false);
  const [scanTarget, setScanTarget] = useState('');
  const [scanType, setScanType] = useState<ScanType>('ssl');
  const [startingScan, setStartingScan] = useState(false);

  // Advanced scan options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [crawlDepth, setCrawlDepth] = useState(3);
  const [maxUrls, setMaxUrls] = useState(500);
  const [includeSubdomains, setIncludeSubdomains] = useState(false);
  const [enableAuth, setEnableAuth] = useState(false);
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [authenticatedPaths, setAuthenticatedPaths] = useState('');

  const [previousScans, setPreviousScans] = useState<SecurityScan[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const scansPerPage = 10;

  // Handle rescan navigation state
  useEffect(() => {
    const state = location.state as { openScanModal?: boolean; prefillData?: any } | null;
    if (state?.openScanModal && state?.prefillData) {
      const { target, type, config } = state.prefillData;

      // Set target and type
      setScanTarget(target || '');
      setScanType(type || 'ssl');

      // Set advanced options if pentest
      if (type === 'pentest' && config) {
        setShowAdvancedOptions(true);
        setCrawlDepth(config.depth || 3);
        setMaxUrls(config.maxUrls || 500);
        setIncludeSubdomains(config.includeSubdomains || false);

        if (config.authentication?.credentials) {
          setEnableAuth(true);
          setAuthUsername(config.authentication.credentials.username || '');
          setAuthPassword(config.authentication.credentials.password || '');
          setLoginUrl(config.authentication.loginUrl || '');
          setAuthenticatedPaths((config.authentication.authenticatedPaths || []).join('\n'));
        }
      }

      // Open modal
      setShowNewScanModal(true);

      // Clear state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    loadData();
  }, []);

  // Polling mais rápido quando tiver scan rodando
  useEffect(() => {
    const hasRunningScans = scans.some(s => s.status === 'running' || s.status === 'pending');
    const interval = setInterval(loadData, hasRunningScans ? 3000 : 30000); // 3s quando rodando
    return () => clearInterval(interval);
  }, [scans]);

  // Notifica quando scan completa
  useEffect(() => {
    if (previousScans.length > 0) {
      scans.forEach(scan => {
        const prev = previousScans.find(p => p.id === scan.id);
        if (prev && prev.status === 'running' && scan.status === 'completed') {
          const findings = scan.findings_count || 0;
          const critical = scan.critical_count || 0;
          const high = scan.high_count || 0;

          if (critical > 0 || high > 0) {
            toast.error(
              `⚠️ ${scan.name || scan.target} completou!\n${critical} críticos, ${high} altos`,
              { duration: 8000 }
            );
          } else if (findings > 0) {
            toast.success(
              `✅ ${scan.name || scan.target} completou!\n${findings} vulnerabilidades encontradas`,
              { duration: 5000 }
            );
          } else {
            toast.success(
              `🎉 ${scan.name || scan.target} completou!\nNenhuma vulnerabilidade encontrada`,
              { duration: 5000 }
            );
          }
        } else if (prev && prev.status === 'running' && scan.status === 'failed') {
          toast.error(`❌ ${scan.name || scan.target} falhou!`, { duration: 5000 });
        }
      });
    }
    setPreviousScans(scans);
  }, [scans]);

  const loadData = async () => {
    try {
      // Carrega scans primeiro (mais importante)
      const scansRes = await securityScanService.getAll();
      if (scansRes.success) setScans(scansRes.data);

      // Tenta carregar dashboard, mas não bloqueia se falhar
      try {
        const dashboardRes = await securityScanService.getDashboard();
        if (dashboardRes.success) setDashboard(dashboardRes.data);
      } catch (dashError) {
        console.warn('Dashboard error (non-blocking):', dashError);
        // Calcula dashboard local se o endpoint falhar
        if (scansRes.success && scansRes.data.length > 0) {
          const localScans = scansRes.data;
          setDashboard({
            totalScans: localScans.length,
            runningScans: localScans.filter(s => s.status === 'running').length,
            completedScans: localScans.filter(s => s.status === 'completed').length,
            totalFindings: localScans.reduce((sum, s) => sum + (s.findings_count || 0), 0),
            openFindings: localScans.reduce((sum, s) => sum + (s.critical_count || 0) + (s.high_count || 0), 0),
            criticalFindings: localScans.reduce((sum, s) => sum + (s.critical_count || 0), 0),
            highFindings: localScans.reduce((sum, s) => sum + (s.high_count || 0), 0),
            avgSecurityScore: localScans.filter(s => s.security_score != null).reduce((sum, s, _, arr) => sum + Number(s.security_score || 0) / arr.length, 0),
            recentScans: localScans.slice(0, 10),
            findingsBySeverity: {},
          });
        }
      }
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartScan = async () => {
    console.log('[DEBUG] handleStartScan called, target:', scanTarget, 'type:', scanType);

    if (!scanTarget) {
      toast.error(t('messages.targetRequired'));
      return;
    }

    const toastId = toast.loading(`Iniciando scan ${scanType.toUpperCase()}...`);

    try {
      setStartingScan(true);

      // Build config with advanced options
      const config: Record<string, any> = {};

      // Only add crawling config for pentest type
      if (scanType === 'pentest' && showAdvancedOptions) {
        config.depth = crawlDepth;
        config.maxUrls = maxUrls;
        config.includeSubdomains = includeSubdomains;

        // Add authentication if enabled
        if (enableAuth && authUsername && authPassword) {
          // Parse authenticated paths (one per line)
          const paths = authenticatedPaths
            .split('\n')
            .map(p => p.trim())
            .filter(p => p.length > 0);

          config.authentication = {
            type: 'form',
            loginUrl: loginUrl || undefined,
            credentials: {
              username: authUsername,
              password: authPassword,
            },
            authenticatedPaths: paths.length > 0 ? paths : undefined,
          };
        }
      }

      console.log('[DEBUG] Calling securityScanService.startScan with config:', config);
      const result = await securityScanService.startScan(scanType, scanTarget, Object.keys(config).length > 0 ? config : undefined);
      console.log('[DEBUG] Result:', result);

      if (result && result.success) {
        toast.success(`Scan iniciado! Analisando ${scanTarget}`, { id: toastId });
        setShowNewScanModal(false);
        resetScanForm();
        setActiveTab('scans');
        await loadData();
      } else {
        console.error('[DEBUG] Result not successful:', result);
        toast.error('Falha ao iniciar scan', { id: toastId });
      }
    } catch (error: any) {
      console.error('[DEBUG] Error starting scan:', error);
      toast.error(error?.response?.data?.error || error?.message || t('messages.errorStartingScan'), { id: toastId });
    } finally {
      setStartingScan(false);
    }
  };

  const resetScanForm = () => {
    setScanTarget('');
    setScanType('ssl');
    setShowAdvancedOptions(false);
    setCrawlDepth(3);
    setMaxUrls(500);
    setIncludeSubdomains(false);
    setEnableAuth(false);
    setAuthUsername('');
    setAuthPassword('');
    setLoginUrl('');
    setAuthenticatedPaths('');
  };

  const handleQuickAnalysis = async () => {
    if (!scanTarget) {
      toast.error(t('messages.targetRequired'));
      return;
    }

    const toastId = toast.loading('Iniciando análise rápida (SSL + Headers + DNS)...');

    try {
      setStartingScan(true);
      const result = await securityScanService.startQuickAnalysis(scanTarget);
      if (result.success) {
        toast.success(`3 scans iniciados para ${scanTarget}`, { id: toastId });
        setShowNewScanModal(false);
        setScanTarget('');
        setActiveTab('scans');
        await loadData();
      } else {
        toast.error('Falha ao iniciar análise', { id: toastId });
      }
    } catch (error: any) {
      console.error('Error starting quick analysis:', error);
      toast.error(error.response?.data?.error || t('messages.errorStartingScan'), { id: toastId });
    } finally {
      setStartingScan(false);
    }
  };

  const handlePentest = async () => {
    if (!scanTarget) {
      toast.error(t('messages.targetRequired'));
      return;
    }

    const toastId = toast.loading('Iniciando Pentest OWASP Top 10...');

    try {
      setStartingScan(true);
      const result = await securityScanService.startPentest(scanTarget);
      if (result.success) {
        toast.success(`Pentest iniciado! Isso pode levar alguns minutos...`, { id: toastId });
        setShowNewScanModal(false);
        setScanTarget('');
        setActiveTab('scans');
        await loadData();
      } else {
        toast.error('Falha ao iniciar pentest', { id: toastId });
      }
    } catch (error: any) {
      console.error('Error starting pentest:', error);
      toast.error(error.response?.data?.error || t('messages.errorStartingScan'), { id: toastId });
    } finally {
      setStartingScan(false);
    }
  };

  const handleCancelScan = async (scan: SecurityScan) => {
    const toastId = toast.loading(`Cancelando ${scan.name || scan.target}...`);
    try {
      const result = await securityScanService.cancel(scan.id);
      if (result.success) {
        toast.success('Scan cancelado!', { id: toastId });
        await loadData();
      } else {
        toast.error('Falha ao cancelar', { id: toastId });
      }
    } catch (error: any) {
      console.error('Error cancelling scan:', error);
      toast.error(error?.message || 'Erro ao cancelar scan', { id: toastId });
    }
  };

  const handleDeleteScan = async (id: string) => {
    toast((toastInstance) => (
      <div className="flex flex-col space-y-3">
        <p className="font-medium text-white">{t('messages.confirmDelete')}</p>
        <div className="flex space-x-2">
          <button
            onClick={async () => {
              toast.dismiss(toastInstance.id);
              try {
                await securityScanService.delete(id);
                toast.success(t('messages.scanDeleted'));
                loadData();
              } catch (error) {
                console.error('Error deleting scan:', error);
                toast.error(t('messages.errorDeleting'));
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            {t('common:buttons.delete')}
          </button>
          <button
            onClick={() => toast.dismiss(toastInstance.id)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm font-medium"
          >
            {t('common:buttons.cancel')}
          </button>
        </div>
      </div>
    ), {
      duration: 10000,
      position: 'top-center',
    });
  };

  const handleExportPDF = async (scan: SecurityScan) => {
    try {
      toast.loading(t('messages.exportingPDF'), { id: 'export-pdf' });
      const blob = await securityScanService.exportPDF(scan.id);
      const filename = `security-report-${scan.type}-${new Date().toISOString().split('T')[0]}.pdf`;
      securityScanService.downloadBlob(blob, filename);
      toast.success(t('messages.exportSuccess'), { id: 'export-pdf' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('messages.exportError'), { id: 'export-pdf' });
    }
  };

  const handleExportExcel = async (scan: SecurityScan) => {
    try {
      toast.loading(t('messages.exportingExcel'), { id: 'export-excel' });
      const blob = await securityScanService.exportExcel(scan.id);
      const filename = `security-report-${scan.type}-${new Date().toISOString().split('T')[0]}.xlsx`;
      securityScanService.downloadBlob(blob, filename);
      toast.success(t('messages.exportSuccess'), { id: 'export-excel' });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(t('messages.exportError'), { id: 'export-excel' });
    }
  };

  const getScanTypeIcon = (type: string) => {
    switch (type) {
      case 'ssl':
        return <LockClosedIcon className="h-5 w-5" />;
      case 'headers':
        return <ShieldCheckIcon className="h-5 w-5" />;
      case 'port':
        return <ServerIcon className="h-5 w-5" />;
      case 'dns':
        return <GlobeAltIcon className="h-5 w-5" />;
      case 'dast':
        return <DocumentMagnifyingGlassIcon className="h-5 w-5" />;
      case 'pentest':
        return <BoltIcon className="h-5 w-5" />;
      default:
        return <ShieldCheckIcon className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      running: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
    };
    return styles[status] || styles.pending;
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
      case 'high':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 'medium':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'low':
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getScoreColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex-shrink-0">
              <ShieldCheckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {t('title')}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 hidden sm:block">
                {t('subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowNewScanModal(true)}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="h-5 w-5" />
            <span className="font-semibold text-sm sm:text-base">{t('newScan')}</span>
          </button>
        </div>

        {/* Running Scans Banner */}
        {scans.filter(s => s.status === 'running' || s.status === 'pending').length > 0 && (
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="animate-pulse">
                <ArrowPathIcon className="h-5 w-5 text-yellow-600 animate-spin" />
              </div>
              <h3 className="font-semibold text-yellow-700 dark:text-yellow-400">
                Scans em Andamento ({scans.filter(s => s.status === 'running' || s.status === 'pending').length})
              </h3>
            </div>
            <div className="space-y-3">
              {scans.filter(s => s.status === 'running' || s.status === 'pending').map((scan) => (
                <div key={scan.id} className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getScanTypeIcon(scan.type)}
                      <span className="font-medium text-gray-900 dark:text-white">{scan.name || scan.target}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        scan.status === 'running'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {scan.status === 'running' ? 'Executando' : 'Aguardando'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-bold text-violet-600 dark:text-violet-400">{scan.progress}%</span>
                      <button
                        onClick={() => handleCancelScan(scan)}
                        className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Cancelar scan"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${scan.progress}%` }}
                    />
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-400 to-purple-500 rounded-full animate-pulse opacity-50"
                      style={{ width: `${scan.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <ShieldCheckIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="text-xl sm:text-3xl font-bold text-white">{dashboard?.totalScans || 0}</span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.totalScans')}</h3>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <ShieldExclamationIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="text-xl sm:text-3xl font-bold text-white">{dashboard?.criticalFindings || 0}</span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.criticalFindings')}</h3>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <ExclamationTriangleIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="text-xl sm:text-3xl font-bold text-white">{dashboard?.openFindings || 0}</span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.openFindings')}</h3>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <ShieldCheckIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className={`text-xl sm:text-3xl font-bold text-white`}>
                  {dashboard?.avgSecurityScore != null ? Number(dashboard.avgSecurityScore).toFixed(1) : '-'}
                </span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.avgScore')}</h3>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-around sm:justify-start sm:space-x-8 px-2 sm:px-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'overview'
                    ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <ShieldCheckIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('tabs.overview')}</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('scans')}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'scans'
                    ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  {scans.some(s => s.status === 'running') ? (
                    <ArrowPathIcon className="h-5 w-5 animate-spin text-yellow-500" />
                  ) : (
                    <ClockIcon className="h-5 w-5" />
                  )}
                  <span className="hidden sm:inline">{t('tabs.scans')}</span>
                  {scans.some(s => s.status === 'running' || s.status === 'pending') ? (
                    <span className="relative px-1.5 sm:px-2 py-0.5 text-xs font-bold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                      <span className="absolute -top-1 -right-1 h-2 w-2 bg-yellow-500 rounded-full animate-ping" />
                      <span className="absolute -top-1 -right-1 h-2 w-2 bg-yellow-500 rounded-full" />
                      {scans.filter(s => s.status === 'running' || s.status === 'pending').length} rodando
                    </span>
                  ) : (
                    <span className="px-1.5 sm:px-2 py-0.5 text-xs font-bold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {scans.length}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Quick Actions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('quickActions.title')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <button
                      onClick={() => { setShowNewScanModal(true); setScanType('ssl'); }}
                      className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <LockClosedIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{t('scanTypes.ssl')}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{t('scanTypes.sslDesc')}</div>
                      </div>
                    </button>

                    <button
                      onClick={() => { setShowNewScanModal(true); setScanType('headers'); }}
                      className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <ShieldCheckIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{t('scanTypes.headers')}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{t('scanTypes.headersDesc')}</div>
                      </div>
                    </button>

                    <button
                      onClick={() => { setShowNewScanModal(true); setScanType('port'); }}
                      className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                        <ServerIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{t('scanTypes.port')}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{t('scanTypes.portDesc')}</div>
                      </div>
                    </button>

                    <button
                      onClick={() => { setShowNewScanModal(true); setScanType('dns'); }}
                      className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <GlobeAltIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{t('scanTypes.dns')}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{t('scanTypes.dnsDesc')}</div>
                      </div>
                    </button>

                    <button
                      onClick={() => { setShowNewScanModal(true); setScanType('dast'); }}
                      className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <DocumentMagnifyingGlassIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{t('scanTypes.dast')}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{t('scanTypes.dastDesc')}</div>
                      </div>
                    </button>

                    <button
                      onClick={() => { setShowNewScanModal(true); setScanType('pentest'); }}
                      className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                        <BoltIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{t('scanTypes.pentest')}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{t('scanTypes.pentestDesc')}</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Recent Scans */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('recentScans')}</h3>
                  {dashboard?.recentScans && dashboard.recentScans.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recentScans.slice(0, 5).map((scan) => (
                        <div
                          key={scan.id}
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/security/${scan.id}`)}>
                            {getScanTypeIcon(scan.type)}
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 dark:text-white truncate">{scan.target}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{t(`scanTypes.${scan.type}`)}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(scan.status)}`}>
                              {t(`status.${scan.status}`)}
                            </span>
                            {scan.findings_count > 0 && (
                              <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
                                {scan.findings_count} {t('findings')}
                              </span>
                            )}
                            <button
                              onClick={() => navigate(`/security/${scan.id}`)}
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                              title={t('actions.view')}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            {(scan.status === 'running' || scan.status === 'pending') && (
                              <button
                                onClick={() => handleCancelScan(scan)}
                                className="p-1.5 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg transition-colors"
                                title="Cancelar scan"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                            {scan.status === 'completed' && (
                              <>
                                <button
                                  onClick={() => handleExportPDF(scan)}
                                  className="p-1.5 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
                                  title={t('actions.exportPDF')}
                                >
                                  <DocumentArrowDownIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleExportExcel(scan)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                                  title={t('actions.exportExcel')}
                                >
                                  <TableCellsIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {t('noScans')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'scans' && (
              <div>
                {(() => {
                  // Pagination logic
                  const totalPages = Math.ceil(scans.length / scansPerPage);
                  const startIndex = (currentPage - 1) * scansPerPage;
                  const paginatedScans = scans.slice(startIndex, startIndex + scansPerPage);

                  return scans.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                      <ShieldCheckIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t('noScans')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">{t('noScansDescription')}</p>
                    <button
                      onClick={() => setShowNewScanModal(true)}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>{t('newScan')}</span>
                    </button>
                  </div>
                  ) : (
                    <>
                    <div className="space-y-4">
                    {paginatedScans.map((scan) => (
                      <div
                        key={scan.id}
                        className="group rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 hover:shadow-lg transition-all"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex items-start space-x-4 flex-1 min-w-0">
                            <div className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              {getScanTypeIcon(scan.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                                  {scan.target}
                                </h3>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(scan.status)}`}>
                                  {t(`status.${scan.status}`)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {t(`scanTypes.${scan.type}`)} - {formatDate(scan.created_at)}
                              </div>
                              {scan.status === 'running' && (
                                <div className="mt-2">
                                  <div className="flex items-center space-x-2">
                                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-violet-500 rounded-full transition-all duration-500"
                                        style={{ width: `${scan.progress}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-gray-500">{scan.progress}%</span>
                                  </div>
                                </div>
                              )}
                              {scan.status === 'completed' && (
                                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                                  {scan.critical_count > 0 && (
                                    <span className="flex items-center space-x-1 text-red-600">
                                      <ExclamationCircleIcon className="h-4 w-4" />
                                      <span>{scan.critical_count} {t('severity.critical')}</span>
                                    </span>
                                  )}
                                  {scan.high_count > 0 && (
                                    <span className="flex items-center space-x-1 text-orange-500">
                                      <ExclamationTriangleIcon className="h-4 w-4" />
                                      <span>{scan.high_count} {t('severity.high')}</span>
                                    </span>
                                  )}
                                  {scan.medium_count > 0 && (
                                    <span className="flex items-center space-x-1 text-yellow-500">
                                      <ExclamationTriangleIcon className="h-4 w-4" />
                                      <span>{scan.medium_count} {t('severity.medium')}</span>
                                    </span>
                                  )}
                                  {scan.security_score != null && (
                                    <span className={`font-medium ${getScoreColor(Number(scan.security_score))}`}>
                                      {t('score')}: {Number(scan.security_score).toFixed(1)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={() => navigate(`/security/${scan.id}`)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                              title={t('actions.view')}
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            {(scan.status === 'running' || scan.status === 'pending') && (
                              <button
                                onClick={() => handleCancelScan(scan)}
                                className="p-2 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg transition-colors"
                                title="Cancelar scan"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            )}
                            {scan.status === 'completed' && (
                              <>
                                <button
                                  onClick={() => handleExportPDF(scan)}
                                  className="p-2 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
                                  title={t('actions.exportPDF')}
                                >
                                  <DocumentArrowDownIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleExportExcel(scan)}
                                  className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                                  title={t('actions.exportExcel')}
                                >
                                  <TableCellsIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDeleteScan(scan.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title={t('actions.delete')}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Mostrando {startIndex + 1}-{Math.min(startIndex + scansPerPage, scans.length)} de {scans.length} análises
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          «
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ‹
                        </button>

                        {/* Page numbers */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first, last, current, and adjacent pages
                            if (page === 1 || page === totalPages) return true;
                            if (Math.abs(page - currentPage) <= 1) return true;
                            return false;
                          })
                          .map((page, index, arr) => (
                            <span key={page} className="flex items-center">
                              {index > 0 && arr[index - 1] !== page - 1 && (
                                <span className="px-1 text-gray-400">...</span>
                              )}
                              <button
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                  currentPage === page
                                    ? 'bg-violet-600 text-white'
                                    : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                              >
                                {page}
                              </button>
                            </span>
                          ))
                        }

                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ›
                        </button>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          »
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Scan Modal */}
      {showNewScanModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowNewScanModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="relative inline-block w-full max-w-xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">{t('newScan')}</h2>

              <div className="space-y-4">
                {/* Target URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.target')} *
                  </label>
                  <input
                    type="text"
                    value={scanTarget}
                    onChange={(e) => setScanTarget(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>

                {/* Scan Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.scanType')}
                  </label>
                  <select
                    value={scanType}
                    onChange={(e) => {
                      setScanType(e.target.value as ScanType);
                      if (e.target.value !== 'pentest') {
                        setShowAdvancedOptions(false);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  >
                    <option value="ssl">{t('scanTypes.ssl')}</option>
                    <option value="headers">{t('scanTypes.headers')}</option>
                    <option value="port">{t('scanTypes.port')}</option>
                    <option value="dns">{t('scanTypes.dns')}</option>
                    <option value="dast">{t('scanTypes.dast')}</option>
                    <option value="pentest">{t('scanTypes.pentest')} (OWASP Top 10)</option>
                  </select>
                </div>

                {/* Advanced Options Toggle - Only for Pentest */}
                {scanType === 'pentest' && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      className="flex items-center space-x-2 text-sm font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700"
                    >
                      <span>{showAdvancedOptions ? '- Ocultar' : '+ Mostrar'} Opções Avançadas</span>
                    </button>
                  </div>
                )}

                {/* Advanced Options */}
                {scanType === 'pentest' && showAdvancedOptions && (
                  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    {/* Crawling Options */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                        <GlobeAltIcon className="h-4 w-4" />
                        <span>Crawler</span>
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Profundidade
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={crawlDepth}
                            onChange={(e) => setCrawlDepth(parseInt(e.target.value) || 3)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Max URLs
                          </label>
                          <input
                            type="number"
                            min="50"
                            max="5000"
                            step="50"
                            value={maxUrls}
                            onChange={(e) => setMaxUrls(parseInt(e.target.value) || 500)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                      <label className="flex items-center space-x-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeSubdomains}
                          onChange={(e) => setIncludeSubdomains(e.target.checked)}
                          className="w-4 h-4 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Incluir subdomínios</span>
                      </label>
                    </div>

                    {/* Authentication Options */}
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableAuth}
                          onChange={(e) => setEnableAuth(e.target.checked)}
                          className="w-4 h-4 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500"
                        />
                        <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                          <LockClosedIcon className="h-4 w-4" />
                          <span>Scan Autenticado (Opcional)</span>
                        </span>
                      </label>

                      {enableAuth && (
                        <div className="mt-3 space-y-3 pl-6">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Forneça credenciais para escanear áreas protegidas por login.
                          </p>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              URL de Login (opcional)
                            </label>
                            <input
                              type="text"
                              value={loginUrl}
                              onChange={(e) => setLoginUrl(e.target.value)}
                              placeholder="Auto-detectado se vazio"
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Usuário *
                              </label>
                              <input
                                type="text"
                                value={authUsername}
                                onChange={(e) => setAuthUsername(e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Senha *
                              </label>
                              <input
                                type="password"
                                value={authPassword}
                                onChange={(e) => setAuthPassword(e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Páginas para Escanear após Login (uma por linha)
                            </label>
                            <textarea
                              value={authenticatedPaths}
                              onChange={(e) => setAuthenticatedPaths(e.target.value)}
                              placeholder={`/dashboard\n/admin\n/profile\n/api/users`}
                              rows={4}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                            />
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              Se vazio, o crawler navegará automaticamente pelas páginas descobertas.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={handleStartScan}
                  disabled={startingScan || !scanTarget}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-colors disabled:opacity-50"
                >
                  <PlayIcon className="h-5 w-5" />
                  <span>{scanType === 'pentest' && showAdvancedOptions ? 'Iniciar Pentest Avançado' : t('startScan')}</span>
                </button>
                <button
                  onClick={handleQuickAnalysis}
                  disabled={startingScan || !scanTarget}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  <BoltIcon className="h-5 w-5" />
                  <span>{t('quickAnalysis')}</span>
                </button>
              </div>

              <button
                onClick={() => {
                  setShowNewScanModal(false);
                  resetScanForm();
                }}
                className="w-full mt-3 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                {t('common:buttons.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
